import qz from "qz-tray";
import { KEYUTIL, KJUR, hextorstr, stob64 } from "jsrsasign";

import {
  inventoryConstants,
  outletConstants,
  toastMessages,
  toastType,
  printerConstants,
  QZ_KNOWN_ERRORS,
} from "../../../constants";
import {
  customToast,
  formatedDateTime,
  formatOutletSessionId,
  parseToDecimalNumber,
  getCurrencySymbol,
} from "../../utility";
import { printZebraLabel } from "../ZebraPrinter/zebraPrinter";
import {
  getItemFromLocalStorage,
  setItemToLocalStorage,
} from "../../../system/storage";
import { prepareCashManagementReceiptForPrinter } from "./prepareReceiptForPrinter";
import { sentryLogError } from "../../../system/sentry";

qz.security.setCertificatePromise(function (resolve, reject) {
  resolve(process.env.REACT_APP_QZ_DIGITAL_CERTIFICATE);
});

var privateKey = process.env.REACT_APP_QZ_PRIVATE_KEY;

qz.security.setSignatureAlgorithm("SHA512"); // Since 2.1
qz.security.setSignaturePromise(function (toSign) {
  return function (resolve, reject) {
    try {
      var pk = KEYUTIL.getKey(privateKey);
      var sig = new KJUR.crypto.Signature({ alg: "SHA512withRSA" }); // Use "SHA1withRSA" for QZ Tray 2.0 and older
      sig.init(pk);
      sig.updateString(toSign);
      var hex = sig.sign();
      resolve(stob64(hextorstr(hex)));
    } catch (err) {
      console.error(err);
      reject(err);
    }
  };
});

/* As in code the connection with QZ tray is established to Perform 1 batch and we close the connection
  So for cases where connection is not successful, QZ tray will retires the connection and during this 
*/
export const isThrowQzError = (error) => {
  let stringifyError = String(error?.message ?? error);
  let isIgnoreError = false;
  if (
    // qz.websocket.isActive() returns true during connecting (duration of retires on connection failure) and code process get printers, get devices and this triggers undefind error
    stringifyError.includes(QZ_KNOWN_ERRORS.UNDEFINED) ||
    // if user calls disconnect while websocket.connect is in process (duration of connection retries after failure and user changes screen that triggers the disconnect in finally block)
    stringifyError.includes(QZ_KNOWN_ERRORS.CONNECTION_CANCELLED) ||
    stringifyError.includes(QZ_KNOWN_ERRORS.SEND_DATA_NOT_FUNC) ||
    stringifyError.includes(QZ_KNOWN_ERRORS.SEND_DATA_TYPE_ERROR)
  ) {
    isIgnoreError = true;
    // only console if ignoring error
    console.log("From isThrowQzError: ignoring this known error, ", error);
  }

  return !isIgnoreError;
};

// Function to connect to QZ Tray
export const connectToQZTray = async () => {
  try {
    if (qz && !qz.websocket.isActive()) {
      await qz.websocket.connect();
    }

    return qz;
  } catch (error) {
    if (isThrowQzError(error)) {
      // loging on sentry or in console are handled in the parent functions in the whole app
      throw new Error("QZ Tray Error - " + error.message);
    }
  }
};

// Function to disconnect from QZ Tray
export const disconnectFromQZTray = async () => {
  try {
    if (qz.websocket.isActive()) {
      await qz.websocket.disconnect();
    }
  } catch (error) {
    // as there's a race condition on disconnect so if error in disconnect then ignore
    console.log("Disconnecting from QZ Tray Error", error);
  }
};

export const printTestReceiptData = async (
  printer,
  receiptData,
  activateSpinner = () => {},
  deactivateSpinner = () => {}
) => {
  try {
    activateSpinner(inventoryConstants.LABELS_PRINTING);
    await connectToQZTray();

    const foundPrinter = await qz.printers.find(printer);
    if (!foundPrinter) {
      throw new Error(
        "Error during find printer: " + toastMessages.NO_PRINTER_DEVICE_FOUND
      );
    }
    const configuration = qz.configs.create(foundPrinter);

    try {
      await qz.print(configuration, receiptData);
    } catch (error) {
      throw new Error("Error printing:" + error.message);
    }
  } catch (error) {
    if (isThrowQzError(error)) {
      const errorMessage = error?.toString();
      sentryLogError(errorMessage);
      console.error(errorMessage);
      customToast(errorMessage, toastType.ERROR);
    }
  } finally {
    deactivateSpinner(inventoryConstants.LABELS_PRINTING);
    await disconnectFromQZTray();
  }
};

//-------Get Mac Address of the computer device it is used in case of outlet to identify each connected device
export const getMacAddress = async () => {
  //------return if mac address found in local storage
  const localMac = getItemFromLocalStorage(outletConstants.MAC_ADDRESS);
  if (localMac) {
    return localMac;
  }

  //------if mac address not found in local storage then get from qz connection
  try {
    await connectToQZTray();
    const device = await qz?.networking?.device();
    const macAddress = device?.mac;
    if (!macAddress) {
      throw new Error(toastMessages.MAC_ADDRESS_NOT_FOUND);
    }
    setItemToLocalStorage(outletConstants.MAC_ADDRESS, macAddress);
    return macAddress;
  } catch (error) {
    if (isThrowQzError(error)) {
      console.log("getMAxAddress", isThrowQzError(error));
      console.error("Error getting MAC address:", error);
      customToast(error.message, toastType.ERROR);
    }
    return null;
  } finally {
    await disconnectFromQZTray();
  }
};

//Function to print receipt data
export const printReceiptData = async (
  receiptData,
  printer,
  isShowToast = true
) => {
  if (printer) {
    printTestReceiptData(printer.generalSettings.productName, receiptData);
  } else if (isShowToast) {
    customToast(toastMessages.NO_PRINTER_DEVICE_FOUND, toastType.ERROR);
  } else {
    sentryLogError(toastMessages.NO_PRINTER_DEVICE_FOUND);
  }
};

// gets the DPI(dots per inch) for given printer via qzTray.
export const getDpi = async (printer, qzConnection = false) => {
  let dpi = printerConstants.DEFAULT_DPI;
  if (!printer) {
    return dpi;
  }
  try {
    if (!qzConnection) {
      await connectToQZTray();
    }
    const allPrinterDetails = await qz.printers.details();
    if (allPrinterDetails) {
      const currentPrinterDetails = allPrinterDetails.find(
        (p) => p.name === printer?.printerName
      );
      if (currentPrinterDetails && currentPrinterDetails.density) {
        dpi = currentPrinterDetails.density;
      }
    }
  } catch (error) {
    if (isThrowQzError(error)) {
      console.error("Error during get dpi:", error);
      sentryLogError("Error during get dpi: " + error?.toString());
    }
  } finally {
    if (!qzConnection) {
      await disconnectFromQZTray();
    }
  }
  return dpi;
};

export const printZebraData = async (
  labelData,
  labelDesignType,
  storeLogo,
  printer,
  deactivateSpinner
) => {
  try {
    if (!printer) {
      return customToast(
        toastMessages.NO_PRINTER_DEVICE_FOUND,
        toastType.ERROR
      );
    }
    await connectToQZTray();
    const printerName = await qz.printers.find(printer?.printerName);
    // Print EPoS data to the default printer
    let configuration = qz.configs.create(printerName);

    let dpi = await getDpi(printer, true);

    for (let i = 0; i < labelData.length; i++) {
      const { printQuantity } = labelData[i];
      for (let j = 0; j < printQuantity; j++) {
        const label = printZebraLabel(
          labelDesignType,
          labelData[i],
          storeLogo,
          dpi
        );
        try {
          await qz.print(configuration, label);
        } catch (error) {
          customToast(error.toString(), toastType.ERROR);
          console.error("Error printing:", error);
        }
      }
    }
  } catch (error) {
    if (isThrowQzError(error)) {
      customToast(error?.toString(), toastType.ERROR);
      console.error("Error during find printer:", error);
      sentryLogError("Error during find printer: " + error?.toString());
    }
  } finally {
    deactivateSpinner(inventoryConstants.LABELS_PRINTING);
    await disconnectFromQZTray();
  }
};

export const printCashManagementReceipt = (data, printer) => {
  const currencySymbol = getCurrencySymbol(data?.store?.currency);
  const getReceiptBalanceValue = (amount) => {
    return data.type === outletConstants.CASH_OUT ||
      data.type === outletConstants.PETTY_CASH_OUT
      ? `-${currencySymbol}${amount}`
      : `${currencySymbol}${amount}`;
  };
  let printReceiptDataArr = [];
  let prepareReceiptData = {};
  prepareReceiptData = {
    header: {
      receiptTitle: data.type,
      storeName: data.store?.name,
      receiptBalance: getReceiptBalanceValue(data.amount),
    },
  };

  switch (data.type) {
    case outletConstants.CASH_IN:
    case outletConstants.CASH_OUT:
    case outletConstants.PETTY_CASH_IN:
    case outletConstants.PETTY_CASH_OUT:
      prepareReceiptData = {
        ...prepareReceiptData,
        body: [
          {
            leftValue: "Date",
            rightValue: formatedDateTime(new Date().toISOString()),
          },
          { leftValue: "User", rightValue: data.user.name },
          { leftValue: "Register", rightValue: data.outlet.name },
          { leftValue: "Note", rightValue: data.note },
        ],
      };
      printReceiptDataArr = prepareCashManagementReceiptForPrinter(
        prepareReceiptData,
        true
      );
      break;
    case outletConstants.OPEN_REGISTER:
      prepareReceiptData = {
        header: {
          ...prepareReceiptData.header,
          receiptTitle: "Starting Balance",
          receiptBalance: getReceiptBalanceValue(
            parseToDecimalNumber(data.startingBalance)
          ),
        },
        body: [
          { leftValue: "Outlet", rightValue: data.outlet.name },
          {
            leftValue: "Session ID",
            rightValue: formatOutletSessionId(data.sessionCount),
          },
          { leftValue: "User", rightValue: data.openBy.name },
          {
            leftValue: "Opening Time",
            rightValue: formatedDateTime(data.openAt),
          },
        ],
      };
      printReceiptDataArr = prepareCashManagementReceiptForPrinter(
        prepareReceiptData,
        true
      );
      break;
    case outletConstants.CLOSE_REGISTER:
      prepareReceiptData = {
        header: {
          ...prepareReceiptData.header,
          receiptTitle: "Ending Balance",
          receiptBalance: getReceiptBalanceValue(
            parseToDecimalNumber(data.countedBalance)
          ),
        },
        body: [
          { leftValue: "Outlet", rightValue: data.outlet.name },
          {
            leftValue: "Session ID",
            rightValue: formatOutletSessionId(data.sessionCount),
          },
          { leftValue: "Closure", rightValue: data.sessionCount },
          { leftValue: "User", rightValue: data.openBy.name },
          {
            leftValue: "Opening Time",
            rightValue: formatedDateTime(data.openAt),
          },
          {
            leftValue: "Closing Time",
            rightValue: formatedDateTime(data.closeAt),
          },
        ],
      };
      printReceiptDataArr = prepareCashManagementReceiptForPrinter(
        prepareReceiptData,
        true
      );
      break;
    default:
      printReceiptDataArr = null;
  }

  if (printReceiptDataArr) {
    printReceiptData(printReceiptDataArr, printer, true);
  }
};

export const getPrintersListFromQz = async () => {
  try {
    const qz = await connectToQZTray();
    const printers = (await qz.printers.find()) || [];
    const printerList = printers?.map((printer, index) => {
      return { id: index + 1, printerName: printer };
    });
    return printerList;
  } catch (error) {
    if (isThrowQzError(error)) {
      // loging on sentry or in console are handled in the parent functions in the whole app
      throw new Error("QZ Tray Error - " + error.message);
    }
  }
};
